import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import Map, { Source, Layer, NavigationControl, Marker } from "react-map-gl";
import {
  mapConfig,
  mapStyle,
  mapStyles,
  mapboxToken,
  stateCoordinates,
  scrollZoomGuard,
} from "../../config";
import { abbrState, buildFilterOptions, filterSchools, getBottomCenterOffset } from "../../utils";
import states from "../../data/states";
import SchoolPopup from "../../components/SchoolPopup";
import MapButtons from "../MapButtons";
import schoolsData from "../../data/schools";
import * as layers from "../../layers";
import { registerPinImages } from "../../pins";
import { AppFilters, MapFiltersButton, StateMarker } from "./styled.js";
import { Wrapper, Container, MapSearch } from "../../components/App/styled.js";
import { MapFadeIn } from "../../components/MapChrome/styled.js";

function SchoolsMap({ className, ...props }) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  const [caseStudy, setCaseStudy] = useState(null);
  const [school, setSchool] = useState(null);
  const [schools, setSchools] = useState(schoolsData);
  const [stateTotals, setStateTotals] = useState([]);
  const [mapViewState, setMapViewState] = useState(mapConfig);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    systemSize: [],
    solarOwnership: [],
    structureType: [],
    caseStudies: [],
    batteryStorage: [],
    cleanEnergyTechnology: [],
    yearInstalled: []
  });

  const filterOptions = buildFilterOptions(schoolsData);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeSchoolsCircle, setActiveSchoolsCircle] = useState("");
  const [activeClusterLayer1, setActiveClusterLayer1] = useState("");
  const [activeClusterLayer2, setActiveClusterLayer2] = useState("");
  const [activeClusterLayer3, setActiveClusterLayer3] = useState("");
  const [activeClusterLayer4, setActiveClusterLayer4] = useState("");
  const [interacted, setInteracted] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef();
  const resizeObserverRef = useRef();
  const styleLoadedRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.resize();
      mapRef.current?.fitBounds(mapConfig.initialBounds, {
        padding: mapConfig.padding,
        duration: 0,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  // Safety net: reveal the map even if 'styledata' never fires (e.g. a
  // stalled style request), rather than leaving it hidden indefinitely.
  useEffect(() => {
    const timer = setTimeout(() => setMapReady(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const revealMap = useCallback(() => {
    registerPinImages(mapRef.current.getMap());
    mapRef.current?.resize();
    resizeObserverRef.current = new ResizeObserver(() => mapRef.current?.resize());
    resizeObserverRef.current.observe(mapRef.current.getContainer());
    mapRef.current.fitBounds(mapConfig.initialBounds, {
      padding: mapConfig.padding,
      duration: 0,
    });
    setMapReady(true);
  }, []);

  // Reveal as soon as the style (layers/camera) is ready, rather than
  // waiting for 'load', which also waits on the viewport's tiles to finish
  // downloading. Tiles then populate progressively underneath, same as
  // before the fade-in was added.
  const onStyleData = useCallback(
    (event) => {
      if (event.dataType !== "style" || styleLoadedRef.current) return;
      styleLoadedRef.current = true;
      revealMap();
    },
    [revealMap]
  );

  const handleStateCountClick = (event) => {
    mapRef.current.flyTo({
      center: [event.target._lngLat.lng, event.target._lngLat.lat],
      duration: 1000,
      zoom: 5,
    });
  };

  useEffect(() => {
    let stateTotals = [];
    if (schools == null) return;
    schools.forEach(function (x) {
      if (stateTotals[x.properties.state] == null) {
        stateTotals[x.properties.state] = 1;
      } else {
        stateTotals[x.properties.state]++;
      }
    });
    setStateTotals(stateTotals);
  }, [schools]);

  const stateCountMarkers = useMemo(
    () =>
      states.features.map((x, index) => {
        const coordinates = stateCoordinates.find((y) => {
          return y[0] === x.properties.name;
        });
        x.properties.longitude = coordinates[7];
        x.properties.latitude = coordinates[6];
        let color = mapStyles.stateCountTier3Color;
        
        if (parseInt(x.properties.number_solar_schools) < 10) {
          color = mapStyles.stateCountTier1Color;
        } else if (parseInt(x.properties.number_solar_schools) < 50) {
          color = mapStyles.stateCountTier2Color;
        } else if (parseInt(x.properties.number_solar_schools) < 100) {
          color = mapStyles.stateCountTier3Color;
        } else if (parseInt(x.properties.number_solar_schools) < 501) {
          color = mapStyles.stateCountTier4Color;
        } else {
          color = mapStyles.stateCountTier5Color;
        }
        if (!stateTotals[abbrState(x.properties.name)]) {
          return null;
        }
        return (
          <Marker
            key={`marker-${index}`}
            longitude={x.properties.longitude}
            latitude={x.properties.latitude}
            onClick={handleStateCountClick}
            anchor="bottom"
          >
            <StateMarker color={color}>
              {stateTotals[abbrState(x.properties.name)]}
            </StateMarker>
          </Marker>
        );
      }),
    [stateTotals]
  );

  const onClick = useCallback((event) => {
    setInteracted(true);
    const feature = event.features && event.features[0];
    if (!feature) return;

    switch (feature.layer.id) {
      case "schools-circle":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-cluster":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-marker":
        if (!feature.properties.cluster) {
          setSchool(feature.properties);
          setCaseStudy(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: Math.max(mapRef.current.getZoom(), 8),
            offset: getBottomCenterOffset(mapRef.current),
          });
        }
        break;
      case "case-studies-marker":
        if (!feature.properties.cluster) {
          setCaseStudy(feature.properties);
          setSchool(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: Math.max(mapRef.current.getZoom(), 8),
            offset: getBottomCenterOffset(mapRef.current),
          });
        }
        break;
      default:
        break;
    }
  }, []);

  const onMouseEnter = useCallback((event) => {
    const feature = event.features && event.features[0];
    switch (feature.layer.id) {
      case "schools-marker":
        break;
      case "case-studies-marker":
        break;
      case "schools-circle":
        setActiveSchoolsCircle(feature.properties.id);
        break;
      default:
        break;
    }
    if (feature?.layer.id === "schools-cluster") {
      const pointCount = feature.properties.point_count;
      if (pointCount <= 50) {
        setActiveClusterLayer1(feature.properties.cluster_id);
      } else if (pointCount <= 99) {
        setActiveClusterLayer2(feature.properties.cluster_id);
      } else if (pointCount <= 249) {
        setActiveClusterLayer3(feature.properties.cluster_id);
      } else {
        setActiveClusterLayer4(feature.properties.cluster_id);
      }
    }
  }, []);

  const onMouseLeave = useCallback((event) => {
    setActiveSchoolsCircle("");
    setActiveClusterLayer1("");
    setActiveClusterLayer2("");
    setActiveClusterLayer3("");
    setActiveClusterLayer4("");
  }, []);

  const handleToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
  };

  const handleSearch = (searchTerm) => {
    setActiveSearchTerm(searchTerm);
  };

  useEffect(() => {
    setSchools(filterSchools(activeSearchTerm, activeFilters, schoolsData));
  }, [activeSearchTerm, activeFilters]);

  // Close an open popup once its marker drops out of the filtered/searched
  // result set, rather than leaving it pointing at a marker that's no
  // longer on the map.
  useEffect(() => {
    if (school && !schools.some((x) => x.properties.id === school.id)) {
      setSchool(null);
    }
  }, [schools, school]);

  useEffect(() => {
    if (caseStudy && !schools.some((x) => x.properties.id === caseStudy.id)) {
      setCaseStudy(null);
    }
  }, [schools, caseStudy]);

  return (
    <>
      <MapFiltersButton activeFilters={activeFilters} results={schools} onClick={handleToggle} />
      <Wrapper>
        <MapSearch
          placeholder={isMobile ? "Search here" : "Search by school name, district, or city"}
          onSearch={handleSearch}
        />
        <Container>
          <AppFilters
            filterOptions={filterOptions}
            activeFilters={activeFilters}
            results={schools}
            isOpen={filtersOpen}
            onToggle={handleToggle}
            onFilter={handleFilter}
          />
          <MapFadeIn $ready={mapReady}>
          <Map
            {...mapViewState}
            id="schoolsMap"
            ref={mapRef}
            onStyleData={onStyleData}
            onMove={(evt) => setMapViewState(evt.viewState)}
            scrollZoom={scrollZoomGuard === "click-to-activate" ? interacted : true}
            cooperativeGestures={scrollZoomGuard === "cooperative"}
            width="100%"
            height="100%"
            mapStyle={mapStyle}
            mapboxAccessToken={mapboxToken}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            interactiveLayerIds={[
              "schools-marker",
              // "case-studies-marker",
              "schools-circle",
              "schools-cluster",
            ]}
          >
            <NavigationControl showCompass={false} />
            <Source id="states" type="geojson" data={states}>
              <Layer {...layers.stateBordersSchoolsLayer} />
            </Source>
            <Source
              id="schools-data"
              type="geojson"
              data={{ type: "FeatureCollection", features: schools }}
              cluster={true}
              clusterMaxZoom={6}
              clusterRadius={50}
            >
              <Layer {...layers.schoolsMarkerLayer} />
              {/* <Layer {...layers.caseStudiesMarkerLayer} /> */}
              <Layer {...layers.schoolsCircleLayer} />
              <Layer
                {...layers.schoolsCircleHoverLayer}
                filter={["==", "id", activeSchoolsCircle]}
              />
              <Layer {...layers.schoolsClusterLayer} />
              <Layer
                {...layers.clustersHoverLayer1}
                filter={["==", "cluster_id", activeClusterLayer1]}
              />
              <Layer
                {...layers.clustersHoverLayer2}
                filter={["==", "cluster_id", activeClusterLayer2]}
              />
              <Layer
                {...layers.clustersHoverLayer3}
                filter={["==", "cluster_id", activeClusterLayer3]}
              />
              <Layer
                {...layers.clustersHoverLayer4}
                filter={["==", "cluster_id", activeClusterLayer4]}
              />
              <Layer {...layers.schoolsClusterCountLayer} />
              {mapViewState.zoom < 5 && stateCountMarkers}
            </Source>
            {caseStudy && (
              <SchoolPopup
                anchor="bottom"
                longitude={Number(caseStudy.longitude)}
                latitude={Number(caseStudy.latitude)}
                onClose={() => setCaseStudy(null)}
                closeOnClick={false}
                data={caseStudy}
              ></SchoolPopup>
            )}
            {school && (
              <SchoolPopup
                anchor="bottom"
                longitude={Number(school.longitude)}
                latitude={Number(school.latitude)}
                onClose={() => setSchool(null)}
                closeOnClick={false}
                data={school}
              ></SchoolPopup>
            )}
          </Map>
          </MapFadeIn>
          <MapButtons mapRef={mapRef} showStates={true} padding={mapConfig.padding} />
        </Container>
      </Wrapper>
    </>
  );
}

export default SchoolsMap;
